import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import SortSpinner from '../../../components/ui/SortSpinner'
import type { EventType } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  /** Görünen satır sayısı — son satırda "aşağı" pasif olması için. */
  rowCount: number
  /** Yeniden sıralama sürüyor mu — oklar kilitlenir. */
  reordering: boolean
  onMove: (row: EventType, dir: 'up' | 'down') => void
  onEdit: (row: EventType) => void
  onToggle: (row: EventType, active: boolean) => void
  onDelete: (row: EventType) => void
}

/**
 * Etkinlik türü tablosunun sütunları (paylaşılan ui/DataTable'a beslenir). Baştaki **sıra** kolonu yan yana
 * ↑/↓ okları (ortak SortSpinner) — sıra numarası gösterilmez, taşıma anında kalıcı. Durum satır-içi
 * InputSwitch (Aktif/Pasif) + satır aksiyonları (düzenle/sil). **Korumalı sistem kaydı yoktur.**
 * (022 StakeholderColumns deseniyle aynı.)
 */
export function eventTypeColumns({
  t,
  localized,
  busyId,
  rowCount,
  reordering,
  onMove,
  onEdit,
  onToggle,
  onDelete,
}: ColumnOpts): ReactElement[] {
  return [
    // Sıra kolonu — yalnızca oklar (SORT_INDEX değeri gösterilmez); liste daima sıra düzeninde.
    <Column
      key="sort"
      header={t('activity.eventTypes.columns.sortIndex')}
      style={{ width: '6rem' }}
      body={(row: EventType, { rowIndex }) => (
        <SortSpinner
          onUp={() => onMove(row, 'up')}
          onDown={() => onMove(row, 'down')}
          disableUp={rowIndex === 0}
          disableDown={rowIndex === rowCount - 1}
          busy={reordering}
          labelUp={t('common.reorder.up')}
          labelDown={t('common.reorder.down')}
        />
      )}
    />,
    <Column
      key="name"
      header={t('activity.eventTypes.columns.name')}
      body={(row: EventType) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="status"
      header={t('activity.eventTypes.columns.status')}
      style={{ width: '8rem' }}
      body={(row: EventType) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('activity.eventTypes.status.active')
              : t('activity.eventTypes.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('activity.eventTypes.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: EventType) => {
        const active = row.recordStatus === 1
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={t('activity.eventTypes.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-pencil"
              rounded
              text
              aria-label={t('activity.eventTypes.actions.edit')}
              tooltip={t('activity.eventTypes.actions.edit')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onEdit(row)}
            />
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              aria-label={t('activity.eventTypes.actions.delete')}
              tooltip={t('activity.eventTypes.actions.delete')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={busyId === row.id}
              onClick={() => onDelete(row)}
            />
          </div>
        )
      }}
    />,
  ]
}
